import React, { createContext, useContext, useState, useCallback } from 'react';

const MusicPlayerContext = createContext({
  currentMusic: null,
  playlist: [],
  playing: false,
  setCurrentMusic: () => {},
  setPlaylist: () => {},
  addToPlaylist: () => {},
  removeFromPlaylist: () => {},
  playMusicNow: () => {},
  primePlayMusic: () => {},
  playNext: () => {},
  playPrevious: () => {},
  clearPlaylist: () => {},
  showPlaylist: false,
  setShowPlaylist: () => {}
});

export const MusicPlayerProvider = ({ children }) => {
  const [currentMusic, setCurrentMusic] = useState(null);
  const [playlist, setPlaylist] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);

  const addToPlaylist = useCallback((music) => {
    setPlaylist(prev => {
      // 避免重复添加
      if (prev.find(m => m.id === music.id)) {
        return prev;
      }
      return [...prev, music];
    });
  }, []);

  /**
   * 触发“用户手势保活”的首播（不负责改 playlist）
   * 用于解决：首次进站点击播放，因异步 loadedmetadata 丢失手势导致 play() 被拦截。
   */
  const primePlayMusic = useCallback((music) => {
    if (!music) return;
    try {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('app:player-prime-play', { detail: { music } }));
      }
    } catch (_) {}
  }, []);

  /**
   * 从“列表/搜索/详情页”等入口直接播放某首歌：
   * - addToPlaylist（可选）
   * - setCurrentMusic + setPlaying(true)
   * - 同步 primePlay，尽量保证第一次就能成功播放
   */
  const playMusicNow = useCallback((music, options = {}) => {
    if (!music) return;
    const shouldAdd = options?.addToPlaylist !== false;
    try { if (shouldAdd) addToPlaylist(music); } catch (_) {}
    setCurrentMusic(music);
    setPlaying(true);
    primePlayMusic(music);
  }, [addToPlaylist, primePlayMusic]);

  const removeFromPlaylistById = useCallback((musicId) => {
    setPlaylist(prev => {
      const newPlaylist = prev.filter(m => m.id !== musicId);
      // 如果删除的是当前播放的音乐，播放下一首
      if (currentMusic && currentMusic.id === musicId) {
        if (newPlaylist.length > 0) {
          const currentIndex = prev.findIndex(m => m.id === musicId);
          const nextIndex = currentIndex < newPlaylist.length ? currentIndex : newPlaylist.length - 1;
          setCurrentMusic(newPlaylist[nextIndex]);
        } else {
          setCurrentMusic(null);
          setPlaying(false);
        }
      }
      return newPlaylist;
    });
  }, [currentMusic]);

  /**
   * 播放下一首
   * - 若传入了目标曲目（来自播放器内部随机/顺序选择），则直接切到该曲目
   * - 否则（外部简单调用）按播放列表顺序切换
   */
  const playNext = useCallback((nextMusic = null) => {
    if (nextMusic && typeof nextMusic === 'object') {
      setCurrentMusic(nextMusic);
      return;
    }
    if (!playlist || playlist.length === 0) return;
    const currentIndex = playlist.findIndex(m => m.id === currentMusic?.id);
    const nextIndex = currentIndex < playlist.length - 1 ? currentIndex + 1 : 0;
    setCurrentMusic(playlist[nextIndex]);
  }, [playlist, currentMusic]);

  /**
   * 播放上一首
   * - 若传入了目标曲目（来自播放器内部随机/顺序选择），则直接切到该曲目
   * - 否则按播放列表顺序切换
   */
  const playPrevious = useCallback((prevMusic = null) => {
    if (prevMusic && typeof prevMusic === 'object') {
      setCurrentMusic(prevMusic);
      return;
    }
    if (!playlist || playlist.length === 0) return;
    const currentIndex = playlist.findIndex(m => m.id === currentMusic?.id);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : playlist.length - 1;
    setCurrentMusic(playlist[prevIndex]);
  }, [playlist, currentMusic]);

  const clearPlaylist = useCallback(() => {
    setPlaylist([]);
    setCurrentMusic(null);
    setPlaying(false);
  }, []);

  const handlePlaylistChange = useCallback((music, index) => {
    // playlist 点击切歌也视为用户主动播放，尝试 prime 一次更稳
    playMusicNow(music, { addToPlaylist: false });
  }, [playMusicNow]);

  return (
    <MusicPlayerContext.Provider
      value={{
        currentMusic,
        playlist,
        playing,
        setCurrentMusic,
        setPlaylist,
        addToPlaylist,
        removeFromPlaylist: removeFromPlaylistById,
        playMusicNow,
        primePlayMusic,
        playNext,
        playPrevious,
        clearPlaylist,
        handlePlaylistChange,
        setPlaying,
        showPlaylist,
        setShowPlaylist
      }}
    >
      {children}
    </MusicPlayerContext.Provider>
  );
};

export const useMusicPlayer = () => {
  const context = useContext(MusicPlayerContext);
  if (!context) {
    throw new Error('useMusicPlayer must be used within MusicPlayerProvider');
  }
  return context;
};

